/**
 * Services. One entry per page under /services/*.
 *
 * ─── SCOPE IS A TRUST DECISION, NOT A MENU ─────────────────────────────────────
 * `tier` is the important field.
 *
 *   'core'      we do this ourselves, tonight, with the crew and kit we have
 *   'onRequest' we will quote it, and if a specialist does it better we say so
 *   'later'     not offered. Kept here so it can be switched on when capability
 *               arrives, exactly like the claims gate in site.ts
 *
 * This company has $970 of lifetime revenue, no W-2 crew yet, $300k GL and no
 * workers' comp. A six-service menu implies multiple crews and floor equipment we
 * do not own — the warehouse copy previously claimed "ride-on and walk-behind
 * scrubbers", which was invented to fill a page.
 *
 * That is the same failure as the "10+ years" line on the old Wix site, just harder
 * to spot. One question about strip-and-wax that we answer vaguely teaches a facility
 * manager we were overselling, and the entire trust argument dies with it.
 *
 * "On request" is not a hedge. Said plainly it reads as confidence, and it is true.
 * ───────────────────────────────────────────────────────────────────────────────
 */

export type ServiceTier = 'core' | 'onRequest' | 'later';

export interface Service {
  slug: string;
  tier: ServiceTier;
  name: string;
  /** Nav + card one-liner. Plain words, no marketing verbs. */
  blurb: string;
  /** The buyer's actual worry this service answers. */
  worry: string;
  /** <h1> on the detail page — outcome, not category. */
  headline: string;
  intro: string;
  includes: string[];
  /** Answers "how do you charge for this?" without making them call. */
  pricingNote: string;
  /**
   * The questions a buyer actually asks about THIS service, answered on the page and
   * mirrored into FAQPage structured data.
   *
   * Two jobs at once. It is the honest answer to a real question, and it is the only
   * thing that gets these pages past ~330 words — which is what they were, and which
   * is too thin to rank for anything. An answer here must be true and specific; a
   * padded one is worse than a short page.
   */
  faqs: { q: string; a: string }[];
  featured?: boolean;
}

export const SERVICES: Service[] = [
  {
    slug: 'nightly-janitorial',
    tier: 'core',
    name: 'Nightly janitorial',
    blurb: 'The recurring after-hours clean — offices, clinics, and shared spaces.',
    worry: 'Will they actually show up, every night, without me chasing them?',
    headline: 'The same crew, every night, after you lock up',
    intro:
      'This is the core of what we do. A set crew arrives after your team leaves, works a written checklist, and locks up behind themselves. You get a record of it in the morning.',
    includes: [
      'Restrooms cleaned, sanitised, and dispensers restocked',
      'All trash collected and liners replaced',
      'High-touch points disinfected — handles, switches, rails, shared desks',
      'Floors vacuumed, swept, and mopped',
      'Break room and kitchen surfaces, sinks, and appliance fronts',
      'Lights off, doors locked, alarm set — signed off every visit',
    ],
    pricingNote:
      'Priced per square foot, per visit, and the rate drops as frequency rises. Published on the pricing page.',
    faqs: [
      {
        q: 'How do I know the crew actually showed up?',
        a: 'Every visit is clocked in on site and closes with a checklist signed by the person who did the work — what was cleaned, by whom, and when. It goes to you. The most common complaint about the last cleaning company is almost always some version of “I could never tell if they came”, and a record is the only thing that answers it.',
      },
      {
        q: 'Is it the same people every night?',
        a: 'Yes. A set crew is assigned to your building and a named supervisor owns the account. You are not re-explaining your floor plan, your alarm code or which door sticks to a different stranger every week.',
      },
      {
        q: 'What does nightly janitorial cost?',
        a: 'It is priced per square foot per visit, and the rate falls as frequency rises — a building cleaned five nights a week never gets dirty enough to need the deep work a building cleaned every other week does. Office space runs roughly $0.08–$0.14 per square foot per visit with a $175 visit minimum. The full rate card by facility type is published on the pricing page.',
      },
      {
        q: 'Do we have to sign an annual contract?',
        a: 'No. Month to month, 30 days notice either way, no early-termination fee. Long lock-ins exist to protect the cleaning company from its own service quality.',
      },
    ],
    featured: true,
  },
  {
    // CUT. Needs daytime staff we do not have — and it contradicts the name outright.
    // A day porter works the FIRST shift. Dropping it makes the brand more coherent,
    // not less complete.
    slug: 'day-porter',
    tier: 'later',
    name: 'Day porter',
    blurb: 'Someone on site during business hours, keeping the place presentable.',
    worry: 'The lobby and restrooms look rough by lunchtime and nobody is there to fix it.',
    headline: 'Someone on site while the building is busy',
    intro:
      'A porter covers the hours a nightly clean cannot reach — restroom checks through the day, spills, lobby resets, trash on a busy floor, and whatever the building throws at you.',
    includes: [
      'Restroom checks and restocks on a set rotation',
      'Lobby, entry glass, and common-area resets',
      'Spill and incident response',
      'Trash and recycling through the day',
      'Meeting-room turnarounds',
      'Escorting deliveries or vendors where you need it',
    ],
    pricingNote: 'Quoted hourly against the coverage window you need. Half-days and split shifts are normal.',
    faqs: [
      {
        q: 'Is this offered?',
        a: 'Not yet. A day porter works the first shift, which is the opposite of what this company is built around, and staffing it properly needs daytime crew we do not have.',
      },
    ],
  },
  {
    // Needs a burnisher and an auto-scrubber. Quoted case by case until we own them.
    slug: 'floor-care',
    tier: 'onRequest',
    name: 'Floor care',
    blurb: 'Strip and wax, burnishing, and carpet extraction on a planned cycle.',
    worry: 'The floors have gone dull and grey and I do not know who to ask.',
    headline: 'Floors brought back, then kept that way',
    intro:
      'Hard floors and carpet both degrade on a predictable schedule. We put that schedule in writing so it happens before anyone complains, instead of after.',
    includes: [
      'Strip and wax on VCT and hard floors',
      'Burnishing between strip cycles',
      'Carpet hot-water extraction and spot treatment',
      'Grout cleaning and sealing',
      'Concrete scrubbing and sealing',
      'Entry-mat programme so grit stays out of the building',
    ],
    pricingNote: 'Strip and wax starts at $0.80 per square foot. Final price depends on floor condition and coats.',
    faqs: [
      {
        q: 'How often do hard floors need to be stripped and waxed?',
        a: 'For most commercial VCT, once or twice a year with burnishing in between. Stripping more often than that removes finish you paid for; leaving it longer means the finish is gone and the tile itself starts taking the wear, which is a far more expensive problem.',
      },
      {
        q: 'Why does the price vary so much per square foot?',
        a: 'Strip and wax starts at $0.80 per square foot, but the final number depends on the condition the floor is in and how many coats it needs. A floor that has been maintained takes fewer coats than one that has not been touched in four years. We quote after seeing it rather than guessing on the phone.',
      },
      {
        q: 'Can you do this without shutting us down for a day?',
        a: 'That is the reason we work after hours. Strip and wax needs the floor empty and dry while it cures, which is exactly what an overnight window gives you. The floor is walkable before your team arrives.',
      },
    ],
  },
  {
    // Needs a crew surge we do not have. Quoted case by case.
    slug: 'post-construction',
    tier: 'onRequest',
    name: 'Post-construction',
    blurb: 'Final clean on a build-out or renovation, before handover.',
    worry: 'The trades are done Friday and the tenant walks in Monday.',
    headline: 'From dusty build-out to handover-ready',
    intro:
      'Construction dust settles for days after the trades leave. A final clean is a different job from a nightly clean — different equipment, different sequence, and it has to survive a walkthrough.',
    includes: [
      'Full dust removal, high to low, including ledges and diffusers',
      'Window and frame cleaning, interior',
      'Sticker, adhesive, paint, and grout haze removal',
      'Fixture, cabinet, and millwork detailing',
      'Floor scrub and initial finish',
      'Final punch-walk with your PM',
    ],
    pricingNote:
      '$0.40 per square foot for a vacant space with normal construction debris. Hauling, exterior work, and repeat phases are bid separately.',
    faqs: [
      {
        q: 'What is included in a post-construction clean?',
        a: 'The final clean before handover: construction dust off every surface including the ones nobody looks at, adhesive and sticker residue off glass and fixtures, fixtures and cabinetry wiped inside and out, and floors cleaned to a finished state. It is a different job from a recurring clean and is priced separately.',
      },
      {
        q: 'How much does it cost?',
        a: '$0.40 per square foot for a vacant space with normal construction debris. Hauling, exterior work and anything involving hazardous material is quoted separately — those are genuinely different jobs and folding them into one square-foot number is how a price gets padded.',
      },
      {
        q: 'Can you start the recurring service afterwards?',
        a: 'Yes, and we quote them as two separate numbers on purpose. Bundling the final clean into the first month of a recurring contract is a common way to make the ongoing rate look lower than it is.',
      },
    ],
  },
  {
    // Core: low equipment barrier, same crew, same visit. The EPA dwell-time and SDS
    // claims on this page are gated — see SITE.claims.
    slug: 'disinfection',
    tier: 'core',
    name: 'Disinfection',
    blurb: 'Targeted disinfection for clinical spaces and after an outbreak.',
    worry: 'Someone was sick in the office and I need more than a wipe-down.',
    headline: 'Disinfection you can document',
    intro:
      'We use EPA-registered disinfectants at their stated dwell time — which is the part most people skip, and the part that decides whether it worked. You get a record of what was treated and with what.',
    includes: [
      'EPA-registered products used at labelled dwell time',
      'High-touch point mapping for your specific layout',
      'Electrostatic or fogging application where it suits the space',
      'Written record of product, area, and time',
      'Safety data sheets on file and available on request',
      'Clinical protocols for exam and treatment rooms',
    ],
    pricingNote: 'Quoted per visit against the area and protocol. Ongoing clinical disinfection is folded into a contract rate.',
    faqs: [
      {
        q: 'How is disinfection different from a normal clean?',
        a: 'A normal clean removes soil. Disinfection kills organisms on a surface, which requires the right product, the right dwell time, and a documented order of operations. Wiping a surface with a disinfectant and immediately drying it does almost nothing — dwell time is the whole mechanism.',
      },
      {
        q: 'Can you document it for an inspection?',
        a: 'Yes. The disinfection steps are itemised on the visit checklist rather than assumed, with the product and the areas covered recorded. That record is the form an inspector actually asks for.',
      },
      {
        q: 'Someone was sick in our office. Can you come out?',
        a: 'That is a per-visit job, quoted against the area and the protocol needed. Ongoing clinical disinfection for a medical or dental practice is folded into the contract rate instead, because at that frequency it is part of the standard visit rather than a callout.',
      },
    ],
  },
  {
    // Entirely different equipment. Quoted case by case.
    slug: 'windows-and-pressure-washing',
    tier: 'onRequest',
    name: 'Windows & pressure washing',
    blurb: 'Interior and exterior glass, entryways, sidewalks, and dumpster pads.',
    worry: 'The front of the building is the first thing clients see and it looks neglected.',
    headline: 'The parts of the building your clients see first',
    intro:
      'Entry glass, the walkway, and the parking-lot approach do more for a first impression than anything inside. They are also the easiest things to let slide.',
    includes: [
      'Interior glass and partition cleaning',
      'Exterior glass, ground floor and reachable elevations',
      'Entryway and sidewalk pressure washing',
      'Dumpster pad and service-area washdown',
      'Awning and signage rinse',
      'Gum and stain removal at entries',
    ],
    pricingNote: 'Quoted per job or folded into a recurring schedule — quarterly is the usual rhythm.',
    faqs: [
      {
        q: 'How often should entry glass and sidewalks be done?',
        a: 'Quarterly is the usual rhythm for most buildings, and it is worth putting on a schedule rather than calling it in when someone notices. The reason is simple: by the time the front of a building looks bad enough to prompt a phone call, every visitor for the previous two months has already seen it.',
      },
      {
        q: 'What does it cover?',
        a: 'Interior and exterior glass, entryways, sidewalks and dumpster pads. Quoted per job, or folded into a recurring schedule at a lower effective rate.',
      },
      {
        q: 'Do you do high-rise or rope-access window work?',
        a: 'No. We do ground-level and low-rise glass. Anything needing rope access or a lift is a specialist trade and we will say so rather than take the job — finding that out afterwards is far more expensive for you than hearing it now.',
      },
    ],
  },
];

/** Everything we actually publish a page for. Excludes 'later'. */
export function shippedServices(): Service[] {
  return SERVICES.filter((s) => s.tier !== 'later');
}

/** The three we do ourselves. */
export function coreServices(): Service[] {
  return SERVICES.filter((s) => s.tier === 'core');
}

/** The three we will quote, openly, and may bring a specialist in for. */
export function onRequestServices(): Service[] {
  return SERVICES.filter((s) => s.tier === 'onRequest');
}

export function serviceBySlug(slug: string): Service | undefined {
  const found = SERVICES.find((s) => s.slug === slug);
  // A 'later' service has no page — it 404s rather than 301s, because it was never
  // published in the first place.
  return found && found.tier !== 'later' ? found : undefined;
}
